<script setup lang="ts">
import { useRoute, RouterLink } from 'vue-router'
import { useQuery, useSubscription } from '@vue/apollo-composable'
import { ref, watchEffect, computed, watch, nextTick, inject, onMounted } from 'vue'
import dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { cloneDeep, isNil, isUndefined, merge } from 'lodash'
import { isNumeric, sleep } from '@/utils/util.ts'
import { GetChatListPreviewQuery } from '@/components/chat/graphql/chats.queries.ts'
import type {
  ChatList,
  ChatListItem as IChatListItem,
} from '@/components/chat/interfaces/graphql.interfaces.ts'
import type { ChatListItem } from '@/components/chat/interfaces/graphql.interfaces.ts'
import type {
  ChatWSMessage,
  CustomerConnectionStatus,
  CustomerTypingStatus
} from '@/components/chat/interfaces/chat.interfaces.ts'
import {
  CHATS_DEFAULT_PAGE_NUMBER_ON_LOAD,
  CHATS_PAGE_SIZE,
  CUSTOMER_TYPING_MESSAGE_RESET_ANIMATION_DELAY_MS,
  MINIMAL_COMFORT_ADMIN_WIDTH_PX,
} from '@/config/config.ts'
import { useUserStore } from '@/stores/user.store.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
import ChatCard from '@/components/chat/chatV2/ChatCard.vue'
import ChatPagination from '@/components/chat/chatV2/ChatPagination.vue'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { ChevronUpIcon } from '@heroicons/vue/20/solid'
import {
  GET_CHAT_MESSAGES_SUBSCRIPTION,
  GET_CUSTOMER_CONNECTION_STATUS_SUBSCRIPTION, GET_CUSTOMER_TYPING_STATUS_SUBSCRIPTION
} from '@/components/chat/graphql/subscriptions.graphql.ts'
import router from '@/router.ts'

dayjs.extend(isToday)
dayjs.extend(customParseFormat)

const props = defineProps<{
  projectId: number
}>()
const route = useRoute()
const { t } = useTranslations()
const emit = defineEmits(['onScrollToTopRequired', 'onChatSelected'])
const selectedChatIndex = computed<number>(() => {
  return isNumeric(route.params.chatIndex) ? Number(route.params.chatIndex) : -1
})
const customerTypingAnimationTimeoutsMap: Record<string, ReturnType<typeof setTimeout> | null> = {}
const hasSmallScreenResolution = ref<boolean>(window.innerWidth < MINIMAL_COMFORT_ADMIN_WIDTH_PX)
const chatLinkRefs = ref<HTMLDivElement[]>([])
const chatListItems = ref<ChatListItem[]>([])
const availableChatListSize = ref<number>(0)
const queryVariables = ref<{
  input: {
    projectIds: number[]
    pageSize: number
    page: number
  }
}>({
  input: {
    projectIds: [props.projectId],
    pageSize: CHATS_PAGE_SIZE,
    page: CHATS_DEFAULT_PAGE_NUMBER_ON_LOAD,
  },
})
const { getCurrentProjectKey } = useUserStore()
const firstChatAutoselectLatestProjectId: null | number  = null

const totalPages = computed<number>(() => {
  return Math.ceil(availableChatListSize.value / CHATS_PAGE_SIZE)
})

function generateChatLink(chatIndex: number): string {
  const linkPattern = `/${getCurrentProjectKey()}/chats/:chatIndex`
  return linkPattern.replace(':chatIndex', String(chatIndex))
}

function setChatListItem(chatList: IChatListItem[]): void {
  chatListItems.value = chatList
}
function setAvailableChatListSize(total: number): void {
  availableChatListSize.value = total
}

const chatListScrollableDivRef = ref<HTMLDivElement | null>(null)

function scrollChatListToTop() {
  if (chatListScrollableDivRef.value instanceof HTMLDivElement) {
    chatListScrollableDivRef.value.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }
}

async function onNewMessagesReceived(messages: ChatWSMessage[]) {
  for (const message of messages) {
    const chatIndex = chatListItems.value.findIndex(({ chatIndex }) => {
      return chatIndex === message.chatIndex && message.projectId === props.projectId
    })

    const lastMessage = {
      content: message.content,
      createdAt: message.createdAt,
      event: message.event,
    }

    if (chatIndex >= 0 && !isUndefined(chatListItems.value[chatIndex])) {
      chatListItems.value[chatIndex].isFresh = false
      chatListItems.value[chatIndex].isCustomerOnline = message.isCustomerOnline
      chatListItems.value[chatIndex].isAiControlIntercepted = message.isAiControlIntercepted
      chatListItems.value[chatIndex].lastMessage = lastMessage

      continue
    }
    scrollChatListToTop()
    await sleep(650) // TODO: Remove Magic number later or redo animation!

    chatListItems.value.unshift({
      chatId: message.chatId,
      chatKey: message.chatKey,
      chatIndex: message.chatIndex,
      isFresh: message.isFresh,
      isCustomerOnline: message.isCustomerOnline,
      isAiControlIntercepted: message.isAiControlIntercepted,
      contactPhones: null,
      contactEmails: null,
      lastMessage,
    })
  }
}

function changePage (page: number): ChatListItem[] {
  queryVariables.value.input.page = page

  const startIndex = (page - 1) * CHATS_PAGE_SIZE
  const endIndex = startIndex + CHATS_PAGE_SIZE

  return chatListItems.value.slice(startIndex, endIndex)
}

function updateCustomerTypingStatusByChatKey(key: string, isTyping: boolean): void {
  const chatIndex = chatListItems.value.findIndex(({ chatKey }) => chatKey === key)

  if (chatIndex >= 0) {
    chatListItems.value[chatIndex] = merge(chatListItems.value[chatIndex], {
      isTyping,
    })

    if (!isNil(customerTypingAnimationTimeoutsMap[key])) {
      clearTimeout(customerTypingAnimationTimeoutsMap[key] as unknown as number)
    }

    customerTypingAnimationTimeoutsMap[key] = setTimeout(() => {
      updateCustomerTypingStatusByChatKey(key, false)
    }, CUSTOMER_TYPING_MESSAGE_RESET_ANIMATION_DELAY_MS)
  }
}

function updateCustomerConnectionStatusByChatKey(key: string, isOnline: boolean): void {
  const chatIndex = chatListItems.value.findIndex(({ chatKey }) => chatKey === key)

  if (chatIndex >= 0) {
    chatListItems.value[chatIndex] = merge(chatListItems.value[chatIndex], {
      isCustomerOnline: isOnline,
    })
  }
}


watch(() => props.projectId, async () => {
  queryVariables.value.input.page = CHATS_DEFAULT_PAGE_NUMBER_ON_LOAD
  queryVariables.value.input.projectIds = [props.projectId]
  await refetchChatList(queryVariables.value)
})

/*  watchEffect(() => {
  console.log('\n\nv2 ChatEffect | route.params.chatIndex', route.params.chatIndex)

  selectedChatIndex.value = isNumeric(route.params.chatIndex)
    ? Number(route.params.chatIndex)
    : chatListItems.value[0]?.chatIndex ?? -1

  // console.log('oof:', selectedChatIndex.value, )

  if (selectedChatIndex.value) {
    const selectedChat = chatListItems.value.find(({ chatIndex }) => chatIndex === selectedChatIndex.value)

    if (selectedChat) {
      emit('onChatSelected', {
        contactPhones: selectedChat.contactPhones,
        contactEmails: selectedChat.contactEmails,
        chatIndex: selectedChat.chatIndex,
      })
    }
  }
})

watch(() => chatLinkRefs.value[0], (newVal) => {
  const isElementExist = newVal instanceof HTMLDivElement
  const isFirstPage = queryVariables.value.input.page === CHATS_DEFAULT_PAGE_NUMBER_ON_LOAD
  const isSameProject = firstChatAutoselectLatestProjectId === props.projectId

  console.log('isElementExist', isElementExist, 'isFirstPage', isFirstPage, '!isSameProject', !isSameProject, 'GGG', route.params.chatIndex)

  if (isElementExist && isFirstPage && !isSameProject && !isNumeric(route.params.chatIndex)) {
    console.log('ups', newVal.getElementsByTagName('a')[0])

    firstChatAutoselectLatestProjectId = props.projectId
    void nextTick(() => {
      newVal.getElementsByTagName('a')[0]?.click()
    })
  }
}, { immediate: true}) */

// Initialization
const {
  onResult: onResultChatListQuery,
  onError: onErrorChatListQuery,
  refetch: refetchChatList
} = useQuery<{ chatList: ChatList }>(
  GetChatListPreviewQuery,
  { variables: queryVariables },
)

onResultChatListQuery((result) => {
  setChatListItem(cloneDeep(result.chatList.data)) // TODO Later: Use best Graphql Approaches
  setAvailableChatListSize(result.chatList.total)
})

onErrorChatListQuery((error) => {
  console.log('onError:', error)
})

onMounted(async () => {
  await router.isReady()
})

// GraphQL: Chat messages subscriptions
const {
  result: newMessages,
  onError: onNewMessagesSubscriptionFailed
} = useSubscription<{ chatMessages: ChatWSMessage[] }>(GET_CHAT_MESSAGES_SUBSCRIPTION, { fetchPolicy: 'no-cache' })

watch(newMessages, (newMessagesValue) => {
  if (!newMessagesValue) {
    return
  }

  onNewMessagesReceived(newMessagesValue.chatMessages)
})

// GraphQL: Customer connection status subscriptions
const {
  result: customerConnectionStatus
} = useSubscription<{ customerConnectionStatus: CustomerConnectionStatus }>(GET_CUSTOMER_CONNECTION_STATUS_SUBSCRIPTION, { fetchPolicy: 'no-cache' })

watch(customerConnectionStatus, (statusValue) => {
  if (!statusValue) {
    return
  }

  updateCustomerConnectionStatusByChatKey(statusValue.customerConnectionStatus.chatKey, statusValue.customerConnectionStatus.isOnline)
})

// GraphQL: Customer typing status
const {
  result: customerTypingStatus,
} = useSubscription<{ customerTypingStatus: CustomerTypingStatus }>(GET_CUSTOMER_TYPING_STATUS_SUBSCRIPTION, { fetchPolicy: 'no-cache' })

watch(customerTypingStatus, (statusValue) => {
  if (!statusValue) {
    return
  }

  updateCustomerTypingStatusByChatKey(statusValue.customerTypingStatus.chatKey, true)
})
</script>

<!-- TODO: These buttons still needed to be implemented -->
<!-- <div class="flex justify-between p-4">
   <button
      type="button"
      class="text-white bg-[#6D4DFF] hover:bg-indigo-300 focus:ring-4 focus:outline-hidden focus:bg-indigo-200 font-medium rounded-lg text-sm px-2 w-1/4 py-2.5 text-center inline-flex items-center me-2"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
        class="size-6 mr-2"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M9.348 14.652a3.75 3.75 0 0 1 0-5.304m5.304 0a3.75 3.75 0 0 1 0 5.304m-7.425 2.121a6.75 6.75 0 0 1 0-9.546m9.546 0a6.75 6.75 0 0 1 0 9.546M5.106 18.894c-3.808-3.807-3.808-9.98 0-13.788m13.788 0c3.808 3.807 3.808 9.98 0 13.788M12 12h.008v.008H12V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
        />
      </svg>
      Live
    </button>
    <button
      type="button"
      class="text-white bg-[#6D4DFF] hover:bg-indigo-300 focus:ring-4 focus:outline-hidden focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
        class="size-6 mr-2"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"
        />
      </svg>
      Archived
    </button>
  </div> -->

<template>
  <div class="flex flex-col mx-auto w-full max-w-md md:h-[calc(100vh-14rem)] max-h-[1500px] rounded-2xl bg-white">
    <Disclosure v-slot="{ open, close }" :defaultOpen="true" as="div">
      <DisclosureButton
        v-if="hasSmallScreenResolution"
        class="flex w-full justify-between rounded-lg px-4 py-2 text-left text-sm font-medium hover:bg-white-200"
      >
        <span>{{ t('chats.page.availableChats') }}</span>
        <ChevronUpIcon
          :class="open ? 'rotate-180 transform' : ''"
          class="sm:hidden h-5 w-5 text-gray-500"
        />
      </DisclosureButton>
      <DisclosurePanel as="div" ref="chatListScrollableDivRef" class="pt-2 text-sm text-gray-500">
        <div class="h-[calc(100vh-6rem)] xl:h-[calc(100vh-18rem)] max-h-[calc(100vh-10rem)] md:max-h-[1400px] max-md:max-h-[calc(100vh-20rem)] overflow-y-auto pt-1">
          <p class="text-xs text-black md:text-sm font-semibold text-center pb-4">
            {{ t('chats.page.chatsOverview') }} </p>
          <div class="pb-2 pr-2" v-for="chat in chatListItems" :key="chat.chatIndex" ref="chatLinkRefs">
            <RouterLink :to="generateChatLink(chat.chatIndex)" @click="hasSmallScreenResolution && close()">
              <ChatCard
                v-bind="chat"
                :class="{
                  'outline-2 outline-offset-2 outline-double outline-purple-custom': chat.chatIndex == selectedChatIndex,
                }"
              />
            </RouterLink>
          </div>
        </div>
        <ChatPagination
          class="max-w pt-4"
          @changePage="changePage"
          :totalPages="totalPages"
          :currentPage="queryVariables.input.page"
        />
      </DisclosurePanel>
    </Disclosure>
  </div>
</template>
